import React from 'react';
/**
 * A custom React hook to manage a selection of items. It provides a variety of utility
 * functions to add, remove, check, and clear selected items efficiently.
 *
 * @template T - The type of elements being managed in the selection.
 * @returns {UseSelection<T>} An object containing the current selection state and functions
 * to interact with it.
 *
 * @example
 * const { isSelected, selectOne, deselectOne } = useSelection<string>();
 * selectOne('item1');
 * deselectOne('item1');
 * isSelected('item1');
 */
export const useSelection = ({ initialSelected, getIdentifier = JSON.stringify }) => {
    const [selected, setSelected] = React.useState(initialSelected !== null && initialSelected !== void 0 ? initialSelected : []);
    const selectOne = (selectValue) => {
        setSelected((selectedValues) => selectedValues.filter((selectedValue) => getIdentifier(selectedValue) !== getIdentifier(selectValue)).concat(selectValue));
    };
    const selectMany = (selectValues) => {
        const selectValuesIdentifiers = selectValues.map(getIdentifier);
        setSelected((selectedValues) => selectedValues.filter((selectedValue) => !selectValuesIdentifiers.includes(getIdentifier(selectedValue))).concat(selectValues));
    };
    const isSelected = (value) => {
        const selectedIdentifiers = selected.map(getIdentifier);
        return selectedIdentifiers.includes(getIdentifier(value));
    };
    const deselectOne = (deselectValue) => {
        setSelected((selectedValues) => selectedValues.filter((selectedValue) => getIdentifier(selectedValue) !== getIdentifier(deselectValue)));
    };
    const deselectMany = (deselectValues) => {
        const deselectValuesIdentifiers = deselectValues.map(getIdentifier);
        setSelected((selectedValues) => selectedValues.filter((selectedValue) => !deselectValuesIdentifiers.includes(getIdentifier(selectedValue))));
    };
    const deselectAll = () => setSelected([]);
    return {
        selected,
        setSelected,
        isSelected,
        selectOne,
        selectMany,
        deselectOne,
        deselectMany,
        deselectAll,
    };
};
